import { useMemo, memo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function Contact({ solarPropImage }) {
  const { t } = useTranslation();

  const contactData = useMemo(
    () => t("components.home.contact", { returnObjects: true }),
    [t]
  );

  return (
    <section className="relative bg-black min-h-[300px] overflow-hidden">
      <div className="container mx-auto px-4 py-8 relative">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, x: -100, scale: 1.2 }}
            whileInView={{ opacity: 1, x: 0, scale: 0.9 }}
            transition={{
              duration: 0.8,
              scale: {
                duration: 1.2,
                ease: "easeOut",
              },
            }}
            viewport={{ once: true }}
            className="hidden md:flex w-full md:w-1/2 justify-center items-center"
          >
            <div className="relative md:h-[400px] md:w-[500px] flex items-center justify-center">
              <img
                src={solarPropImage}
                alt={contactData.imageAlt || "Solar Panel Installation"}
                className="max-w-full h-auto mx-auto"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="w-full md:w-1/2 pl-4"
          >
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-6 leading-tight">
              {contactData.heading}
            </h1>
            <p className="text-md lg:text-lg text-gray-300 mb-8 leading-relaxed">
              {contactData.description}
            </p>
            <Link to={contactData.link}>
              <motion.button
                whileHover={{ scale: 1.1, backgroundColor: "#009fd6" }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white px-6 py-3 rounded-lg transition-colors duration-300 flex items-center gap-2 cursor-pointer"
                aria-label={contactData.buttonText || "Contact Us"}
              >
                <span>{contactData.buttonText}</span>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

Contact.propTypes = {
  solarPropImage: PropTypes.string.isRequired,
};

export default memo(Contact);
